/**
 * "Remove repair" — for a repair saved on the wrong device (job #092678199:
 * the bypass rebuild was first recorded on the test-only 3" main). Office
 * only, reason required. The server deletes the repair record and voids the
 * provisional repair points for that device on the job, helpers included;
 * approved points are left for payroll. Mockup region 2b,
 * mockups/repair-without-photos.html.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Trash2 } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { apiErrorInfo, type JobAssetRow, type JobRepairResult } from "./types";

export function RemoveRepairDialog({
    open,
    onClose,
    jobId,
    asset,
    repair,
}: {
    open: boolean;
    onClose: () => void;
    jobId: string;
    asset: JobAssetRow | null;
    repair: JobRepairResult | null;
}) {
    const qc = useQueryClient();
    const [reason, setReason] = useState("");

    const remove = useMutation({
        mutationFn: () =>
            api.post<{ voidedPoints: number; approvedPointsLeft: number }>(
                `/api/backflow/job/${jobId}/devices/${asset?.asset_id}/remove-repair`,
                { reason: reason.trim() },
            ),
        onSuccess: (res) => {
            const voided = res?.voidedPoints ?? 0;
            toast.success(
                voided > 0
                    ? `Repair removed. ${voided} repair point line${voided === 1 ? "" : "s"} voided.`
                    : "Repair removed.",
            );
            if ((res?.approvedPointsLeft ?? 0) > 0) {
                toast.warning(
                    "Some repair points for this device were already approved. Payroll needs a point adjustment.",
                    { duration: 12_000, closeButton: true },
                );
            }
            qc.invalidateQueries({ queryKey: ["job", jobId] });
            qc.invalidateQueries({ queryKey: ["backflow", "job", jobId, "repairs"] });
            qc.invalidateQueries({ queryKey: ["activity-timeline"] });
            setReason("");
            onClose();
        },
        onError: (e: unknown) => {
            const { message, error } = apiErrorInfo(e);
            toast.error(message ?? `Couldn't remove the repair: ${error ?? "unknown error"}`);
        },
    });

    const when = repair?.created_at ? new Date(repair.created_at).toLocaleDateString() : null;
    const canSubmit = !!asset && reason.trim().length > 0 && !remove.isPending;

    return (
        <Dialog open={open} onClose={onClose} title={`Remove repair — ${asset?.label ?? "device"}`}>
            <div className="space-y-4 px-5 py-4">
                {(repair?.performed_by_name || when) && (
                    <p className="text-sm text-foreground/80">
                        {repair?.performed_by_name && <>Repaired by {repair.performed_by_name}</>}
                        {repair?.performed_by_name && when && " · "}
                        {when}
                    </p>
                )}
                <p className="rounded-md border border-amber-500/40 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    This deletes the repair record for this device on this job. Provisional repair points for this
                    device are voided for everyone they were credited to (helpers too). Approved points are left alone
                    for payroll to adjust.
                </p>
                <div className="space-y-1.5">
                    <Label htmlFor="remove-repair-reason">Why remove it? *</Label>
                    <Textarea
                        id="remove-repair-reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. entered on the wrong device; the repair is recorded on the bypass"
                        rows={2}
                    />
                </div>
                <div className="flex justify-end gap-2 pt-1">
                    <Button variant="outline" onClick={onClose} disabled={remove.isPending}>
                        Cancel
                    </Button>
                    <Button variant="destructive" disabled={!canSubmit} onClick={() => remove.mutate()}>
                        {remove.isPending ? (
                            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                        ) : (
                            <Trash2 className="mr-1 h-4 w-4" />
                        )}
                        Remove repair
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}
